import {
  type Dispatch,
  Fragment,
  type SetStateAction,
  type TransitionEvent,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useLocation } from "react-router-dom";
import {
  Aggregator,
  AnalyticsDataSource,
  AnalyticsResourceType,
  CalcMetricFormat,
  ComparativeFeature,
  type CurrencyCode,
  CurrencyCodes,
  Feature,
  KubernetesMetric,
  type LimitAggregation,
  Metadata,
  Metric,
  type MetricFilter,
  Renderer,
  type ReportConfig,
  RunReportChannelStatus,
  WidgetState,
} from "@doitintl/cmp-models";
import ArrowLeft from "@mui/icons-material/FirstPageRounded";
import ArrowRight from "@mui/icons-material/LastPageRounded";
import {
  Box,
  DialogContent,
  DialogContentText,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ListItemText from "@mui/material/ListItemText";
import intersection from "lodash/intersection";
import isEqual from "lodash/isEqual";
import uniq from "lodash/uniq";

import { handleResponseError } from "../../api/axiosClient";
import { useApiContext } from "../../api/context";
import { cloudAnalyticsText, comparativeDialogText, metricText, reportText } from "../../assets/texts";
import { reportTxt } from "../../assets/texts/CloudAnalytics";
import { useCreateWidgetReportData } from "../../Components/Dashboard/Analytics/hooks";
import FilterDialog from "../../Components/Dialogs/CloudAnalytics/FilterDialog";
import { FilterModeOption } from "../../Components/Dialogs/CloudAnalytics/types";
import { DoitConsoleTitle } from "../../Components/DoitConsoleTitle";
import useReportMixpanel from "../../Components/hooks/cloudAnalytics/reports/useReportMixpanel";
import useReportSaver from "../../Components/hooks/cloudAnalytics/reports/useReportSaver";
import { useCloudAnalyticsTransforms } from "../../Components/hooks/cloudAnalytics/useCloudAnalyticsTransforms";
import { useMetricFormatter } from "../../Components/hooks/cloudAnalytics/useMetricFormatter";
import useFormatter from "../../Components/hooks/useFormatter";
import { useFullHeight } from "../../Components/hooks/useFullHeight";
import { useHistoryWithFallbackUrl } from "../../Components/hooks/useHistoryWithFallbackUrl";
import { useKeyPress } from "../../Components/hooks/useKeyPress";
import useQueryString from "../../Components/hooks/useQueryString";
import useReportCache, { ReportCacheType } from "../../Components/hooks/useReportCache";
import useUnmountEffect from "../../Components/hooks/useUnmountEffect";
import useUpdateEffect from "../../Components/hooks/useUpdateEffect";
import { CircularProgressLoader } from "../../Components/Loader";
import LoadingButton from "../../Components/LoadingButton";
import MixpanelButton from "../../Components/MixpanelButton";
import SaveAsComponent from "../../Components/SaveComponents/SaveAsComponent";
import SaveDialog from "../../Components/SaveComponents/SaveDialog";
import { useSnackbar } from "../../Components/SharedSnackbar/SharedSnackbar.context";
import SimpleDialog from "../../Components/SimpleDialog";
import SplitCostModal from "../../Components/SplitCostModal";
import TemplateCreate from "../../Components/TemplateCreate/TemplateCreate";
import { useAttributionsContext } from "../../Context/AttributionsContext";
import { useAuthContext } from "../../Context/AuthContext";
import { useCustomerContext } from "../../Context/CustomerContext";
import { useHotkeyContext } from "../../Context/HotkeyContext";
import { useIsFeatureEntitled } from "../../Context/TierProvider";
import { useUnsavedChanges } from "../../Context/UnsavedChangesContext";
import { type MetadataOption, type MetricWSnap } from "../../types";
import { consoleErrorWithSentry } from "../../utils";
import { CSPCustomerID } from "../../utils/common";
import { useFullScreen } from "../../utils/dialog";
import mixpanel from "../../utils/mixpanel";
import { usePrevious } from "../../utils/usePrevious";
import { useSlackIntegration } from "../Integrations/Slack/useSlackIntegration";
import { type KeyTypeValues } from "./api";
import { buildAttributionGroupsPayload } from "./attributionGroups/attributionGroupsPayload";
import { type AttributionGroupWithRef } from "./attributionGroups/types";
import { useCloudAnalyticsContext } from "./CloudAnalyticsContext";
import AnalyticsPageContent from "./Content/AnalyticsPageContent";
import {
  useCreateTemplateContext,
  useMetricSplitsContext,
  useReportConfig,
  useReportContext,
  useReportDimensionsContext,
  useReportSaverContext,
  useReportTemplateContext,
} from "./Context";
import { LimitsFilterDialog } from "./dialogs/limitsFilterDialog";
import { type Limit } from "./dialogs/limitsFilterDialog/limitsFilterDialog";
import { MetricFiltersDialog } from "./dialogs/metricFiltersDialog/metricFiltersDialog";
import { NotificationDialog } from "./dialogs/notificationDialog/NotificationDialog";
import TreemapConfirmDialog from "./dialogs/treemapConfirmDialog/TreemapConfirmDialog";
import { refreshCachedReport } from "./handlers/updateWidgets";
import { type NotifyVia } from "./handlers/updateWidgets";
import Info from "./info";
import { ReportConfigKind } from "./reducers/ReportConfigReducer";
import { initialReportState, ReportStateKind, reportStateReducer } from "./reducers/ReportStateReducer";
import GoogleSheetsDialog from "./renderers/GoogleSheetsDialog";
import ReportHeader from "./report/actionBar/ReportHeader";
import { AvaReportPrompt } from "./report/AvaReportPrompt";
import DraggableGroupWrapper from "./report/config/DraggableGroupWrapper";
import { useCreateRunReportChannel, useReportSystemLabels } from "./report/hooks";
import { cancelQuery, executeQueryRequest, type QueryRequest, QueryType } from "./report/ReportQuery";
import {
  accountsParam,
  attributionParam,
  calculatedMetricParamForReport,
  extractCloudProviders,
  parseFilters,
  positionsParams,
  splitsParam,
} from "./report/ReportQueryUtils";
import ReportQuickView from "./report/ReportQuickView/ReportQuickView";
import ReportRightMenu from "./report/ReportRightMenu";
import ReportViewSelect from "./report/ReportViewSelect";
import { type PopoverAnchorEl } from "./report/types";
import { updateReportStats } from "./report/updateReportStats";
import { ReportPathQueryParams } from "./report/utils";
import ReportData, { type ColKeySort } from "./ReportData";
import { useStyles } from "./ReportStyles";
import {
  comparativeValues,
  defaultMetricFilterOperator,
  ErrorCode,
  FixedFilters,
  getBaseMetricLabels,
  getMetricOptions,
  getMetricsCount,
  getReportRowSort,
  getTimeSettings,
  isComparative,
  isEditor,
  isTable,
  IsValidAggregator,
  mapReportConfigStoreToReportConfig,
  MenuProps,
  Positions,
  REPORT_LEFT_PANEL_BOTTOM_HEIGHT,
  REPORT_LEFT_PANEL_WIDTH,
  REPORT_LEFT_PANEL_WIDTH_CLOSED,
  timeIntervalOptions,
} from "./utilities";

type Props = {
  handleRevertReport: () => void;
  attributionGroups: AttributionGroupWithRef[];
  setRawReportData: Dispatch<SetStateAction<any[][]>>;
};

const Report = ({ handleRevertReport, attributionGroups, setRawReportData }: Props) => {
  const api = useApiContext();
  const classes = useStyles();
  const { isMobile: smDown } = useFullScreen();
  const qs = useQueryString();
  const { attributions } = useAttributionsContext();
  const { report } = useReportContext();
  const { currentUser, isDoitEmployee } = useAuthContext({ mustHaveUser: true });

  const { onOpen: showSharedSnackbar } = useSnackbar();
  const { current: touched } = useRef<Set<string>>(new Set());
  const { customer } = useCustomerContext();
  const { loadAnalyticsLabels } = useCloudAnalyticsContext();
  const transforms = useCloudAnalyticsTransforms();
  const [saveAsComponentVisible, setSaveAsComponentVisible] = useState(false);
  const [openNameDialog, setOpenNameDialog] = useState(false);
  const [isGeneratingReportConfig, setIsGeneratingReportConfig] = useState(false);
  const [reportError, setReportError] = useState<{ code: string; message?: string } | undefined>(undefined);
  const [displayCurrency, setDisplayCurrency] = useState<CurrencyCode | null>(null);
  const reportMixpanel = useReportMixpanel();
  const { metricSplits, metricSplitModalData, deleteMetricSplits, metricSplitsInit } = useMetricSplitsContext();
  const { reportConfig, dispatchReportConfig, reportData, reportDataWForecasts } = useReportConfig();
  const { isScheduled, isCached: isSystemLabelCached } = useReportSystemLabels(report);
  const {
    name,
    currency,
    aggregator,
    renderer,
    features,
    timeInterval,
    rowOrder,
    colOrder,
    comparative,
    metric,
    calculatedMetric,
    extendedMetric,
    forecastSettings,
    metricFilters,
    excludePartialData,
    includeCredits,
    limitAggregation,
    extendedMetrics,
    timeRangeOption,
    includeSubtotals,
    dataSource,
    showValuesInFilterSearchResults,
    datahubMetrics,
  } = reportConfig;

  const isEntitledAnalyticsForecasts = useIsFeatureEntitled("analytics:forecasts");

  const handleReportError = useCallback(
    (code: ErrorCode) => {
      setReportError({ code });
    },
    [setReportError]
  );
  const {
    dashboardsContextLoaded,
    cacheReport,
    isReportCacheLoading,
    isReportCacheRecentlyRefreshed,
    rawCacheReport,
    removeReportFromCache,
    reportCacheType,
    userCachedReportWidget,
  } = useReportCache({
    report,
    isTable: isTable(renderer),
    handleReportError,
  });

  const isCachedReport = useMemo(() => {
    if (report.data.type === "custom") {
      return isSystemLabelCached;
    }
    return (
      reportCacheType === ReportCacheType.WidgetCached ||
      (reportCacheType === ReportCacheType.UserCached && userCachedReportWidget?.state !== WidgetState.Failed)
    );
  }, [report.data.type, isSystemLabelCached, reportCacheType, userCachedReportWidget?.state]);

  const reportDuringCachingProcess = Boolean(userCachedReportWidget?.notifyUser && !reportError);

  const [isReportCachedBtnLoading, setIsReportCachedBtnLoading] = useState(false);
  const [isRefreshCacheBtnLoading, setIsRefreshCacheBtnLoading] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);

  const [selectedCalcMetric, setSelectedCalcMetric] = useState<MetricWSnap | null>(null);
  const [switchedToCalcMetric, setSwitchedToCalcMetric] = useState(false);
  const [switchedToExtendMetric, setSwitchedToExtendMetric] = useState(false);
  const [calcMetricsDialogOpen, setCalcMetricsDialogOpen] = useState(false);
  const [metricFiltersDialogOpen, setMetricFiltersDialogOpen] = useState(false);
  const [limitsFilterDialogOpen, setLimitsFilterDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    reportSaver: { hasUnsavedChanges, initialConfig },
  } = useReportSaverContext();
  const [forceRender, setForceRender] = useState(false);
  const createRunReportChannel = useCreateRunReportChannel({ status: RunReportChannelStatus.INITIALIZED });

  // Slack integration status
  const { isConnected: isSlackConnected, isLoading: slackLoading } = useSlackIntegration();

  // extended metrics can be "cost", "usage" or "datahub"
  const extendedMetricMode = useMemo(() => {
    if (!extendedMetric) {
      return null;
    }

    const extMetric = extendedMetrics?.find((m) => m.key === extendedMetric);
    if (extMetric) {
      return extMetric.type;
    }

    const datahubMetric = datahubMetrics?.find((m) => m.key === extendedMetric);
    if (datahubMetric) {
      return "datahub";
    }

    return "cost";
  }, [extendedMetric, extendedMetrics, datahubMetrics]);

  const [colKeySort, setColKeySort] = useState<ColKeySort>();
  const cachedReportData = useCreateWidgetReportData(rawCacheReport, colKeySort, "", { isCachedReport });
  const loadStartTime = useRef(0);
  const [selected, setSelected] = useState<MetadataOption | undefined>();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [treemapDialogOpen, setTreemapDialogOpen] = useState(false);
  const [comparativeDialogOpen, setComparativeDialogOpen] = useState(false);
  const [reportStates, dispatchReportStates] = useReducer(reportStateReducer, initialReportState);
  const { queryRunning, querySuccess, reportReady, mounted, isInit } = reportStates;
  const { disable: setHotKeysDisabled, enable: setHotKeysEnabled, state: hotKeysEnabled } = useHotkeyContext();
  const runShortcutKey = useKeyPress("r", { shiftKey: true });
  const tableViewShortcutKey = useKeyPress("t");
  const heatmapViewShortcutKey = useKeyPress("h");
  const [prevView, setPrevView] = useState(Renderer.TABLE);
  const [googleDialogOpen, setGoogleDialogOpen] = useState(false);
  // indicate case changing from "none" to some comparative type, enforce new request and not just reportData update
  const [comparativeRequest, setComparativeRequest] = useState(false);
  const colSortDisabled = !!(renderer === Renderer.TREEMAP || (comparative && isComparative(comparative)));
  const [countFieldMissing, setCountFieldMissing] = useState(false);
  const [hideLimitAlert, setHideLimitAlert] = useState(false);
  const [reportLeftDrawerOpen, setReportLeftDrawerOpen] = useState(true);
  const [leftDrawerTransitioning, setLeftDrawerTransitioning] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const { ref: chartContainerRef, height: chartContainerHeight } = useFullHeight();
  const [filterPopoverOpenedOnce, setFilterPopoverOpenedOnce] = useState(false);
  const {
    dimensions,
    handleDimensionSelect,
    handleAggregatorChange,
    handleMetricFiltersChange,
    handleLimitsFilterChange,
    handleDeleteDimension,
    handleReportFilterChange,
    handleTreemapChange,
    resetLimit,
    handleMetricChange,
    handleSortComparativeDimensions,
    handleAttributionMdSelect,
    handleDimensionLabelsChange,
    resetDimensions,
  } = useReportDimensionsContext();
  const { createTemplateData } = useCreateTemplateContext();
  const { reportTemplateId } = useReportTemplateContext();
  const controller = useRef(new AbortController());
  const prevReportConfig = usePrevious(reportConfig);
  const { defaultReportConfig } = useCloudAnalyticsContext();

  const parentUrl = useMemo(() => `/customers/${customer.id}/analyze/reports`, [customer.id]);
  const goBackToReports = useHistoryWithFallbackUrl(parentUrl);

  useEffect(() => {
    setForceRender(false);
    setIsRefreshCacheBtnLoading(false);
  }, [rawCacheReport]);

  useEffect(() => {
    if (reportTemplateId) {
      mixpanel.track("analytics.templates.open", { reportTemplateId });
    }
  }, [reportTemplateId]);

  useEffect(() => {
    if (isReportCacheLoading) {
      loadStartTime.current = performance.now();
    } else if (isCachedReport) {
      const loadEndTime = performance.now();
      const totalDurationMs = Math.round(loadEndTime - loadStartTime.current);
      mixpanel.track("analytics.reports.cached-data-loaded", {
        reportId: report.ref.id,
        totalDurationMs,
      });
    }
  }, [isCachedReport, isReportCacheLoading, report.ref.id]);

  useEffect(() => {
    if (userCachedReportWidget?.state === WidgetState.Failed) {
      setReportError({
        code: ErrorCode.FAILED_TO_CACHE,
      });
    }
  }, [userCachedReportWidget]);

  const handleFilterPopoverOpened = useCallback(() => {
    setFilterPopoverOpenedOnce(true);
  }, []);

  useEffect(() => {
    if (showValuesInFilterSearchResults && filterPopoverOpenedOnce) {
      loadAnalyticsLabels();
    }
  }, [loadAnalyticsLabels, filterPopoverOpenedOnce, showValuesInFilterSearchResults]);

  const dimensionsCols = useMemo(
    () => dimensions?.filter((md) => md._position === Positions.COL && md._visible),
    [dimensions]
  );

  const isCurrentUserEditor = isEditor(currentUser.email, report.data);

  const { handleSave, handleSaveName, handleSaveAsComponentSave, handleResetReport } = useReportSaver();

  const reRender = useCallback(() => {
    if (!isTable(renderer)) {
      setRenderKey((key) => key + 1);
    }
  }, [renderer]);

  const onTransitionEnd = useCallback(
    (transitionEvent: TransitionEvent<HTMLDivElement>) => {
      if (transitionEvent.propertyName !== "width") {
        return;
      }
      setLeftDrawerTransitioning(false);
      reRender();
    },
    [reRender]
  );

  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const showForecast = useMemo(
    () => !!reportConfig.forecastSettings && !!isEntitledAnalyticsForecasts,
    [isEntitledAnalyticsForecasts, reportConfig.forecastSettings]
  );

  const trends = useMemo(
    () => intersection(features, [Feature.TREND_UP, Feature.TREND_DOWN, Feature.TREND_NONE]),
    [features]
  );

  // Popover states
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<PopoverAnchorEl | null>({
    widget: null,
    mdUnused: null,
    mdRow: null,
    mdCol: null,
    mdCount: null,
  });

  const handleClose = useCallback(() => {
    setFilterDialogOpen(false);
    setLimitsFilterDialogOpen(false);
    setTreemapDialogOpen(false);
    setGoogleDialogOpen(false);
    setComparativeDialogOpen(false);
    setTimeout(() => {
      setSelected(undefined);
    }, 300);
    setCalcMetricsDialogOpen(false);
    setOpenNameDialog(false);
    if (!createTemplateData.isCreateTemplateOpen) {
      setHotKeysEnabled();
    }
  }, [setHotKeysEnabled, createTemplateData.isCreateTemplateOpen]);

  const { activatePendingPrompt, clearPendingPrompt } = useUnsavedChanges();

  const isCSP = useMemo(() => customer.id === CSPCustomerID, [customer]);

  const showLimitAlert = useMemo(() => {
    if (!dimensions || hideLimitAlert || !reportStates.finishedFirstQueryRun) {
      return false;
    }

    if (metricFilters.length > 0 && metricFilters.some((mf) => mf.metric !== metric)) {
      return true;
    }

    return dimensions?.some(
      (md) =>
        md._limit &&
        md._limit > 0 &&
        md._limitMetric !== null &&
        md._limitMetric !== undefined &&
        md._limitMetric !== metric
    );
  }, [dimensions, hideLimitAlert, reportStates.finishedFirstQueryRun, metricFilters, metric]);

  const cloudProviders = useMemo(() => extractCloudProviders(dimensions), [dimensions]);

  const getMetricFilter = () => {
    if (metricFilters?.length > 0) {
      return metricFilters[0];
    }
    return {
      metric,
      operator: defaultMetricFilterOperator,
      values: [],
    };
  };

  const updateMetricFilters = useCallback(
    (filters: MetricFilter[]) => {
      dispatchReportConfig({ payload: { metricFilters: filters } });
      handleMetricFiltersChange(filters);
    },
    [handleMetricFiltersChange, dispatchReportConfig]
  );

  const handleMetricFilters = (mf: MetricFilter) => {
    if (
      metricFilters === undefined ||
      metricFilters.length === 0 ||
      mf.metric !== metricFilters[0].metric ||
      mf.operator !== metricFilters[0].operator ||
      mf.values.length !== metricFilters[0].values.length ||
      mf.values.findIndex((v, i) => v !== metricFilters[0].values[i]) !== -1
    ) {
      updateMetricFilters([mf]);
    }
    setMetricFiltersDialogOpen(false);
    setSelected(undefined);
  };

  const onLimitsFilterConfirm = (limits: Limit[], aggregation?: LimitAggregation) => {
    handleLimitsFilterChange(limits);
    dispatchReportConfig({ payload: { limitAggregation: aggregation } });
    setLimitsFilterDialogOpen(false);
  };

  const handleDelete = useCallback(
    (dimensions: MetadataOption[]) => {
      handleDeleteDimension(dimensions);
      dimensions.forEach((d) => {
        deleteMetricSplits(d);
      });
      const lastDimension = dimensions.at(-1);
      if (lastDimension?.data?.type === Metadata.METRIC && lastDimension?._metricFilter) {
        dispatchReportConfig({ payload: { metricFilters: [] } });
      }
    },
    [handleDeleteDimension, deleteMetricSplits, dispatchReportConfig]
  );
  const handleCancelLimits = (item?: MetadataOption) => {
    if (item) {
      handleDelete([item]);
    }
    setLimitsFilterDialogOpen(false);
    setSelected(undefined);
  };

  useEffect(() => {
    if (userCachedReportWidget?.state === WidgetState.Processing) {
      setIsRefreshCacheBtnLoading(true);
    }
  }, [userCachedReportWidget?.state]);

  useEffect(() => {
    if (isCachedReport && cachedReportData) {
      reportData.current = cachedReportData;
      setReportError(undefined);
      dispatchReportStates({
        type: ReportStateKind.FINISHED_RUNNING,
        payload: {
          querySuccess: true,
          reportReady: true,
          queryRunning: false,
          finishedFirstQueryRun: true,
        },
      });
      setIsReportCachedBtnLoading(false);
    }
  }, [isCachedReport, cachedReportData, reportData]);

  const handleRun = useCallback(
    async (partialConfig?: Partial<QueryRequest>, customDimensions?: MetadataOption[]) => {
      if (queryRunning || (!hasUnsavedChanges && isCachedReport)) {
        return;
      }

      if (!partialConfig) {
        partialConfig = {};
      }

      const scopedDimensions = customDimensions ?? dimensions ?? [];
      dispatchReportStates({ type: ReportStateKind.RUNNING, payload: {} });

      // Check for override of config state
      const scopedExcludePartialData = partialConfig.excludePartialData ?? excludePartialData;
      const scopedIncludeCredits = partialConfig.includeCredits ?? includeCredits;
      const scopedExtendedMetric = partialConfig.extendedMetric ?? extendedMetric;
      const scopedMetric = partialConfig.metric ?? metric;

      const timeSettings = getTimeSettings(timeRangeOption);
      const { rows, cols, count } = positionsParams(scopedDimensions);
      if (!timeSettings || (aggregator === Aggregator.COUNT && !count)) {
        // invalid custom time range
        dispatchReportStates({ payload: { queryRunning: false } });
        // invalid query, aggregation count requires count param
        aggregator === Aggregator.COUNT && !count && setCountFieldMissing(true);
        return;
      }
      timeSettings.interval = timeInterval;

      const accounts = accountsParam(scopedDimensions, cloudProviders);
      const filteredAttributions = attributionParam(scopedDimensions, attributions);
      const cMetric = calculatedMetricParamForReport(attributions, calculatedMetric?.data);

      const filters = parseFilters(scopedDimensions);

      const attributionGroupsPayload = buildAttributionGroupsPayload({
        attributions,
        dimensions: scopedDimensions,
        attributionGroups,
      });

      controller.current = new AbortController();

      const channelId = await createRunReportChannel();

      const disableAllocationsOptimization =
        !!qs.disableAllocationsOptimization && String(qs.disableAllocationsOptimization) === "true";

      const request: QueryRequest = {
        type: QueryType.REPORT,
        id: report.ref.id,
        accounts,
        timeSettings,
        rows,
        cols,
        count,
        attributions: filteredAttributions,
        attributionGroups: attributionGroupsPayload,
        filters,
        metricFilters,
        currency: currency ?? null,
        metric: scopedMetric,
        extendedMetric: scopedExtendedMetric ?? undefined,
        trends,
        forecast: showForecast,
        forecastSettings: isEntitledAnalyticsForecasts ? forecastSettings : null,
        cloudProviders: cloudProviders ?? null,
        comparative: comparative && isComparative(comparative) ? comparative : null,
        calculatedMetric: cMetric ?? null,
        excludePartialData: scopedExcludePartialData,
        includeCredits: scopedIncludeCredits,
        noAggregate: qs.noAggregate !== undefined,
        limitAggregation,
        dataSource: dataSource === AnalyticsDataSource.CUSTOMER_FEATURES ? AnalyticsDataSource.BILLING : dataSource,
        signal: controller.current.signal,
        channelId,
        disableAllocationsOptimization,
      };

      const splits = splitsParam(metricSplits, rows, cols);
      if (splits?.length > 0) {
        request.splits = splits;
      }

      const before = performance.now();

      try {
        const response = await executeQueryRequest(api, customer.id, request);
        const after = performance.now();
        const totalDurationMs = Math.round(after - before);
        if (response?.status === 200 && response?.data) {
          reportMixpanel.run(totalDurationMs, cloudProviders, response.data?.details);
          if (report.data.type === "custom") {
            updateReportStats(
              request.id,
              response.data.details?.serverDurationMs,
              response.data.details?.totalBytesProcessed,
              totalDurationMs,
              customer.presentationMode?.enabled
            );
          }

          setDisplayCurrency(currency ?? null);
          setComparativeRequest(false);
          dispatchReportStates({ payload: { reportReady: false } });
          const metricOffset = rows.length + cols.length;
          const vals = getMetricOptions(metricOffset, scopedMetric, isCSP, !!count);
          const numMetrics = getMetricsCount(isCSP, scopedMetric, !!count);
          if (rows.length <= 1) {
            dispatchReportConfig({ payload: { includeSubtotals: false } });
          }
          reportData.current = new ReportData({
            data: response.data.rows,
            forecastRows: response.data.forecastRows,
            aggregator,
            rows,
            cols,
            rowOrder,
            colOrder,
            vals,
            transforms,
            features,
            numMetrics,
            comparative,
            includeSubtotals,
            forecastSettings,
          });

          if (reportData.current.getNumRecords() === 0 && trends.length > 0) {
            setReportError({ code: ErrorCode.EMPTY_DUE_TO_TREND });
          } else {
            setReportError(undefined);
          }

          const rowHeaders = reportData.current.getRowHeaders();
          const colHeaders = reportData.current.getColHeaders();
          const metricLabels = getBaseMetricLabels(dataSource, isCSP);
          if (scopedMetric === Metric.CALCULATED) {
            metricLabels.push(calculatedMetric?.data?.name ?? "");
          } else if (scopedMetric === Metric.EXTENDED) {
            metricLabels.push(extendedMetrics?.find((m) => m.key === extendedMetric)?.label ?? "");
          }
          const headerRow = [...rowHeaders, ...colHeaders, ...metricLabels];
          const headerRowLength = headerRow.length;

          if (!showForecast || !response.data.forecastRows) {
            reportDataWForecasts.current = undefined;
            dispatchReportStates({
              type: ReportStateKind.FINISHED_RUNNING,
              payload: {
                queryRunning: false,
                querySuccess: true,
              },
            });
            setSwitchedToCalcMetric(false);
            setSwitchedToExtendMetric(false);
            const withoutTrends = response?.data?.rows?.map((row) => row.slice(0, headerRowLength));
            const withHeaderRow = [headerRow, ...(withoutTrends ?? [])];
            setRawReportData(withHeaderRow);
            return;
          }

          const startIndex = reportData.current.rowStartIndex;
          const fullData = [...(response.data?.rows ?? []), ...response.data.forecastRows.slice(startIndex)];
          reportDataWForecasts.current = new ReportData({
            data: fullData,
            forecastRows: response.data.forecastRows,
            aggregator,
            rows,
            cols,
            rowOrder,
            colOrder,
            vals,
            transforms,
            features,
            numMetrics,
            forecastSettings,
            includeSubtotals,
          });
          dispatchReportStates({
            type: ReportStateKind.FINISHED_RUNNING,
            payload: {
              querySuccess: true,
            },
          });
          setSwitchedToCalcMetric(false);
          setSwitchedToExtendMetric(false);
          const withoutTrends = fullData?.map((row) => row.slice(0, headerRowLength));
          const withHeaderRow = [headerRow, ...(withoutTrends ?? [])];
          setRawReportData(withHeaderRow);
        }
      } catch (error: any) {
        if (error?.name === "CanceledError") {
          const after = performance.now();
          const totalDurationMs = Math.round(after - before);
          reportMixpanel.runCancel(totalDurationMs);
          dispatchReportStates({
            type: ReportStateKind.FINISHED_RUNNING,
            payload: {
              queryRunning: false,
              querySuccess: false,
            },
          });
          request.signal = undefined;
          cancelQuery(api, customer.id, report.ref.id, channelId);
          return;
        }

        handleResponseError(
          error,
          (errorCode, details) => {
            setReportError({
              code: errorCode ?? ErrorCode.UNKNOWN,
              message: error.response?.data?.error?.message,
            });
            dispatchReportStates({
              type: ReportStateKind.FINISHED_RUNNING,
              payload: {
                querySuccess: false,
              },
            });
            setSwitchedToCalcMetric(false);
            setSwitchedToExtendMetric(false);
            setComparativeRequest(false);
            reportData.current = undefined;
            reportDataWForecasts.current = undefined;

            reportMixpanel.runError(errorCode, details);
          },
          [404, 413]
        );
      }

      dispatchReportStates({ payload: { queryRunning: false } });
    },
    [
      queryRunning,
      hasUnsavedChanges,
      isCachedReport,
      excludePartialData,
      includeCredits,
      extendedMetric,
      metric,
      timeRangeOption,
      dimensions,
      aggregator,
      timeInterval,
      cloudProviders,
      attributions,
      calculatedMetric?.data,
      attributionGroups,
      report.ref.id,
      report.data.type,
      metricFilters,
      currency,
      trends,
      showForecast,
      isEntitledAnalyticsForecasts,
      forecastSettings,
      comparative,
      qs.noAggregate,
      qs.disableAllocationsOptimization,
      limitAggregation,
      dataSource,
      metricSplits,
      api,
      customer.id,
      customer.presentationMode?.enabled,
      reportMixpanel,
      isCSP,
      reportData,
      rowOrder,
      colOrder,
      transforms,
      features,
      includeSubtotals,
      reportDataWForecasts,
      setRawReportData,
      dispatchReportConfig,
      extendedMetrics,
      createRunReportChannel,
    ]
  );

  const handleCancelMetricFilters = (item?: MetadataOption) => {
    if (item) {
      handleDelete([item]);
    }
    setMetricFiltersDialogOpen(false);
    setSelected(undefined);
  };

  // Load report config to state once
  useEffect(() => {
    (async () => {
      if (!dimensions || !!initialConfig) {
        return;
      }
      reportMixpanel.open();
      metricSplitsInit(report.data.config?.splits ?? [], dimensions);
      dispatchReportStates({ payload: { mounted: true } });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions]);

  // track changes in report config, used for "run report" alert banner
  useEffect(() => {
    if (mounted) {
      dispatchReportStates({ type: ReportStateKind.REPORT_CHANGED });
    } else {
      dispatchReportStates({ payload: { querySuccess: false } });
    }
  }, [mounted, metricSplits, timeInterval, timeRangeOption, forecastSettings]);

  // Set as initialized once dimensions and calculated metric are ready
  useEffect(() => {
    if (!isInit && dimensions && (!report.data.config?.calculatedMetric || calculatedMetric)) {
      dispatchReportStates({ payload: { isInit: true } });
    }
  }, [isInit, dimensions, report.data.config?.calculatedMetric, calculatedMetric]);

  const renderReport = useCallback(async () => {
    if (reportData.current) {
      if (hasUnsavedChanges) {
        return;
      }

      const metricOffset = reportData.current.rows?.length + reportData.current.cols.length;
      const vals = getMetricOptions(metricOffset, metric, isCSP, aggregator === Aggregator.COUNT);
      await reportData.current.update(aggregator, vals, features, comparative ?? undefined);
      if (reportData.current?.getNumRecords() === 0 && trends.length > 0) {
        setReportError({ code: ErrorCode.EMPTY_DUE_TO_TREND });
      } else {
        setReportError(undefined);
      }
      dispatchReportStates({ payload: { reportReady: true } });
    }
  }, [aggregator, comparative, features, isCSP, metric, reportData, trends.length, hasUnsavedChanges]);

  const saveButtonDisabled = useMemo(
    () =>
      !mounted ||
      report.data.type === AnalyticsResourceType.PRESET ||
      !isCurrentUserEditor ||
      (!hasUnsavedChanges && !!name),
    [hasUnsavedChanges, isCurrentUserEditor, mounted, name, report.data.type]
  );

  useEffect(() => {
    if (!reportData.current) {
      return;
    }
    if (!IsValidAggregator(aggregator, metric)) {
      dispatchReportConfig({ payload: { aggregator: Aggregator.TOTAL } });
      return;
    }

    if (
      comparativeRequest ||
      switchedToCalcMetric ||
      switchedToExtendMetric ||
      hasUnsavedChanges ||
      touched.has("metric") ||
      touched.has("currency")
    ) {
      return;
    }

    if (showForecast) {
      return;
    }
    renderReport();
  }, [
    showForecast,
    metric,
    aggregator,
    trends,
    features,
    calculatedMetric,
    switchedToCalcMetric,
    comparative,
    comparativeRequest,
    isCSP,
    renderReport,
    switchedToExtendMetric,
    dispatchReportConfig,
    reportData,
    hasUnsavedChanges,
    touched,
  ]);

  useEffect(() => {
    if (!reportData.current) {
      return;
    }
    dispatchReportStates({ payload: { reportReady: false } });
    const dataProp = showForecast && reportDataWForecasts.current ? reportDataWForecasts.current : reportData.current;
    if (dataProp) {
      dataProp.setSort(rowOrder, colOrder, colKeySort);
      const t = setTimeout(() => {
        dispatchReportStates({ payload: { reportReady: true } });
      }, 0);
      return () => {
        clearTimeout(t);
      };
    }
  }, [rowOrder, colOrder, showForecast, colKeySort, reportData, reportDataWForecasts]);

  const onSaveCallback = useCallback(async () => {
    if (isCurrentUserEditor && name) {
      await handleSave();
      goBackToReports();
      return true;
    } else {
      setHotKeysDisabled();
      setOpenNameDialog(true);
      return false;
    }
  }, [goBackToReports, handleSave, isCurrentUserEditor, name, setHotKeysDisabled]);

  useEffect(() => {
    if (
      (hasUnsavedChanges || createTemplateData.hasUnsavedChanges) &&
      report.data.type !== AnalyticsResourceType.PRESET &&
      !isDeleting
    ) {
      activatePendingPrompt({
        onConfirmCallback: () => {},
        onSaveCallback: createTemplateData.isCreateTemplateOpen || reportTemplateId ? undefined : onSaveCallback,
      });
    } else {
      clearPendingPrompt();
    }
  }, [
    activatePendingPrompt,
    clearPendingPrompt,
    createTemplateData.hasUnsavedChanges,
    createTemplateData.isCreateTemplateOpen,
    hasUnsavedChanges,
    isDeleting,
    onSaveCallback,
    report.data.type,
    reportTemplateId,
  ]);

  // Update the time interval setting according to changes in columns
  useUpdateEffect(() => {
    const cols = dimensionsCols?.map((md) => md.data.key);
    const newTimeInterval = timeIntervalOptions.find(
      (option) => isEqual(cols, option.visible) || isEqual(cols, option.available)
    );
    if (newTimeInterval) {
      if (newTimeInterval.value !== timeInterval) {
        dispatchReportConfig({ payload: { timeInterval: newTimeInterval.value } });
      }
    }
  }, [dimensionsCols, timeInterval]);

  // Resets countFieldMissing state
  useEffect(() => {
    if (aggregator === Aggregator.COUNT) {
      setCountFieldMissing((prevCountFieldMissing: boolean) => {
        if (prevCountFieldMissing) {
          const countFieldExists =
            !!dimensions && dimensions?.some((md) => md._position === Positions.COUNT && md._visible);
          if (countFieldExists) {
            return false;
          }
        }
        return prevCountFieldMissing;
      });
    }
  }, [dimensions, aggregator]);

  const changeReportFilter = useCallback(
    (filterId: string, type: FilterModeOption, value: string[] | string, inverse?: boolean) => {
      handleReportFilterChange(filterId, type, value, inverse);
    },
    [handleReportFilterChange]
  );

  const openFilterDialog = (filterChip: MetadataOption, open: boolean) => {
    if (filterChip.data.type === Metadata.METRIC) {
      setMetricFiltersDialogOpen(true);
    } else if (filterChip.data.type === Metadata.LIMITS) {
      setLimitsFilterDialogOpen(true);
    } else {
      setFilterDialogOpen(open);
    }
  };

  const handleAddDimension = useCallback(
    (
      dimensions: MetadataOption[],
      simulateClick: boolean = false,
      fromList: boolean = false,
      openDialog: boolean = true,
      filterExistingChip: boolean = false
    ) =>
      (event?: any) => {
        const lastDimension = dimensions.at(-1);
        if (!lastDimension) {
          return;
        }

        if (lastDimension.selectedValue) {
          changeReportFilter(lastDimension.id, FilterModeOption.IS, [
            lastDimension.selectedValue,
            ...(lastDimension._filter ?? []),
          ]);
        }
        if (event?.metaKey || simulateClick) {
          handleDimensionSelect(dimensions, fromList, filterExistingChip);
          if (lastDimension.selectedValue) {
            return;
          }

          if (
            fromList &&
            (lastDimension._position === Positions.UNUSED ||
              lastDimension.data.type === Metadata.ATTRIBUTION ||
              filterExistingChip)
          ) {
            setSelected(lastDimension);
            openFilterDialog(lastDimension, openDialog);
          }
        } else {
          setSelected(lastDimension);
          openFilterDialog(lastDimension, true);
        }
      },
    [changeReportFilter, handleDimensionSelect]
  );

  const handleChangeLabels = useCallback(
    async (values: KeyTypeValues[], position: Positions) => {
      const selectedDimension = await handleDimensionLabelsChange(values, undefined, position);
      if (selectedDimension && position === Positions.UNUSED) {
        setSelected(selectedDimension);
        openFilterDialog(selectedDimension, true);
      }
    },
    [handleDimensionLabelsChange]
  );

  const handleChangeFilter = useCallback(
    (type: FilterModeOption, value: string[] | string, inverse?: boolean) => {
      changeReportFilter(selected?.id ?? "", type, value, inverse);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected?.id]
  );

  const handleChangeToTreemap = useCallback(() => {
    dispatchReportConfig({
      payload: {
        aggregator: Aggregator.TOTAL,
        features: [],
        renderer: Renderer.TREEMAP,
        comparative: ComparativeFeature.NONE,
      },
    });
    handleTreemapChange();
    handleClose();
    reportMixpanel.viewChanged();
    touched.add("rendererSelect");
    reRender();
  }, [reRender, touched, handleClose, handleTreemapChange, dispatchReportConfig, reportMixpanel]);

  const handleCalculatedMetric = useCallback(() => {
    dispatchReportConfig({ payload: { metric: Metric.CALCULATED, calculatedMetric: selectedCalcMetric } });
    setSwitchedToCalcMetric(true);
    updateMetricFilters([]);
    reportMixpanel.calculatedMetricSelect(selectedCalcMetric?.ref.id);
    handleClose();
  }, [selectedCalcMetric, updateMetricFilters, handleClose, dispatchReportConfig, reportMixpanel]);

  useUpdateEffect(() => {
    if (calculatedMetric) {
      dispatchReportConfig({ payload: { aggregator: Aggregator.TOTAL, features: [], metric: Metric.CALCULATED } });

      handleAttributionMdSelect();
      const usedAttributions = uniq(calculatedMetric?.data?.variables?.map((v) => v.attribution.id) ?? []);
      changeReportFilter(`${Metadata.ATTRIBUTION}:${Metadata.ATTRIBUTION}`, FilterModeOption.IS, usedAttributions);
    }
  }, [calculatedMetric]);

  useUpdateEffect(() => {
    handleAggregatorChange(aggregator);
  }, [aggregator]);

  useUpdateEffect(() => {
    if (!switchedToCalcMetric) {
      setSelectedCalcMetric(null);
    }
  }, [switchedToCalcMetric]);

  const sortComparativeDimensions = () => {
    handleSortComparativeDimensions();
  };

  const updateSkuUnitProjectDimensions = (metricVal: Metric) => {
    const skuDimension = dimensions?.find((m) => m.id === `${Metadata.FIXED}:${FixedFilters.SKU}`);
    const unitDimension = dimensions?.find((m) => m.id === `${Metadata.FIXED}:${FixedFilters.UNIT}`);
    const projectDimension = dimensions?.find((m) => m.id === `${Metadata.FIXED}:${FixedFilters.PROJECT}`);

    const combinedDimensions: MetadataOption[] = [];
    skuDimension && combinedDimensions.push(skuDimension);
    unitDimension && combinedDimensions.push(unitDimension);
    const combinedDimensionsExist = combinedDimensions.length > 0;

    if (metricVal === Metric.USAGE) {
      projectDimension && handleDelete([projectDimension]);
      combinedDimensionsExist && handleAddDimension(combinedDimensions, true, false, true, true)();
      dispatchReportConfig({ payload: { renderer: Renderer.ROW_HEATMAP } });

      touched.add("rendererSelect");
    } else if (metricVal !== Metric.EXTENDED) {
      combinedDimensionsExist && handleDelete(combinedDimensions);
      projectDimension && handleDimensionSelect([projectDimension], true, true);
      dispatchReportConfig({ payload: { renderer: Renderer.STACKED_COLUMN_CHART } });
    }
  };

  const handleMetricSelect = (metricVal: Metric, value: MetricWSnap | string) => {
    if (metricVal === Metric.CALCULATED) {
      setSelectedCalcMetric(value as MetricWSnap);
      setCalcMetricsDialogOpen(true);
      return;
    } else if (metricVal === Metric.EXTENDED) {
      dispatchReportConfig({ payload: { metric: metricVal, extendedMetric: value as string } });
      setSwitchedToExtendMetric(true);
    } else {
      dispatchReportConfig({ payload: { extendedMetric: "" } });
    }

    // Simulate click on "Unit" chip when usage is selected
    if (!reportStates.finishedFirstQueryRun && dimensions) {
      updateSkuUnitProjectDimensions(metricVal);
    }
    if (calculatedMetric) {
      handleMetricChange();
      updateMetricFilters([]);
    }
    reportMixpanel.metricSelect(metricVal, value);

    touched.add("metric");
    dispatchReportConfig({ payload: { metric: metricVal, calculatedMetric: null } });
  };

  const handleCurrencyChange = useCallback(
    (newCurrency: CurrencyCode) => {
      touched.add("currency");
      dispatchReportConfig({ payload: { currency: newCurrency } });
    },
    [dispatchReportConfig, touched]
  );

  const handleKubernetesMetricSelect = (metricVal: KubernetesMetric) => {
    dispatchReportConfig({ payload: { extendedMetric: KubernetesMetric[metricVal] } });
  };

  const handleAggregatorSelect = (newAggregator: Aggregator) => {
    if (newAggregator === Aggregator.COUNT) {
      reportData.current = undefined;
      setCountFieldMissing(false);
    } else if (aggregator === Aggregator.COUNT) {
      setCountFieldMissing(false);
    }
    dispatchReportConfig({ payload: { aggregator: newAggregator } });
  };

  const handleResetLimits = () => {
    const mdWasReset = resetLimit();
    if (mdWasReset) {
      showSharedSnackbar({
        message: reportText.LIMITS_FILTER_RESET,
        variant: "info",
        autoHideDuration: 5000,
      });
    }
  };

  const location = useLocation();

  const runOnOpen = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get(ReportPathQueryParams.runOnOpen) === "true";
  }, [location]);

  const cacheThresholdMs = useMemo(() => {
    const threshold = String(qs.cacheThresholdMs);
    return (threshold && parseInt(threshold, 10)) || undefined;
  }, [qs.cacheThresholdMs]);

  const [hasAlreadyRun, setHasAlreadyRun] = useState(false);

  useEffect(() => {
    // automatically run report
    if (
      !hasAlreadyRun &&
      userCachedReportWidget?.state !== WidgetState.Failed &&
      reportStates.isInit &&
      reportStates.mounted &&
      report.data.config &&
      (!report.data.draft || runOnOpen) &&
      !reportStates.firstRender &&
      dashboardsContextLoaded &&
      !isReportCacheLoading
    ) {
      if (!queryRunning) {
        handleRun();
      }
      setHasAlreadyRun(true);
    }
  }, [
    reportStates,
    hasAlreadyRun,
    handleRun,
    queryRunning,
    isCachedReport,
    saveButtonDisabled,
    reportConfig,
    report.data.config,
    report.data.draft,
    runOnOpen,
    dashboardsContextLoaded,
    isReportCacheLoading,
    userCachedReportWidget,
  ]);

  useUpdateEffect(() => {
    if (hotKeysEnabled && runShortcutKey) {
      dispatchReportStates({ payload: { reportReady: false } });

      handleRun();
    }
  }, [runShortcutKey]);

  useUpdateEffect(() => {
    if (hotKeysEnabled && (tableViewShortcutKey || heatmapViewShortcutKey) && renderer !== prevView) {
      const desiredView = tableViewShortcutKey ? Renderer.TABLE : Renderer.HEATMAP;
      const newView = renderer !== desiredView ? desiredView : prevView;
      reportMixpanel.toggleView(newView);
      setPrevView(renderer);
      dispatchReportConfig({ payload: { renderer: newView } });

      touched.add("rendererSelect");
    }
  }, [tableViewShortcutKey, heatmapViewShortcutKey]);

  useUpdateEffect(() => {
    if (!touched.has("rowOrder")) {
      dispatchReportConfig({ payload: { rowOrder: getReportRowSort(renderer) } });
    }
  }, [renderer]);

  useUnmountEffect(() => {
    if (queryRunning) {
      controller.current.abort();
    }
  });

  const renderSheetsDialog = useCallback(() => {
    if (!googleDialogOpen) {
      return null;
    }

    const dataProp = showForecast && reportDataWForecasts.current ? reportDataWForecasts.current : reportData.current;
    if (!dataProp) {
      return null;
    }
    return (
      <GoogleSheetsDialog
        data={dataProp}
        isDialog={true}
        isDialogOpen={true}
        onClose={handleClose}
        aggregator={aggregator}
        metric={metric}
      />
    );
  }, [googleDialogOpen, showForecast, reportDataWForecasts, reportData, handleClose, aggregator, metric]);

  const isKubernetesUtilization = reportConfig.dataSource === AnalyticsDataSource.KUBERNETES_UTILIZATION;

  const baseFormatterOptions = {
    ...(extendedMetricMode && { extendedMetricMode }),
  };

  if (isKubernetesUtilization) {
    baseFormatterOptions.extendedMetricMode = AnalyticsDataSource.KUBERNETES_UTILIZATION;
  }

  const baseFormatter = useFormatter({
    aggregator,
    currency: displayCurrency || currency || CurrencyCodes.USD,
    metric,
    options: baseFormatterOptions,
  });

  const calculatedMetricFormatter = useMetricFormatter(
    aggregator,
    currency || CurrencyCodes.USD,
    calculatedMetric?.data?.format || CalcMetricFormat.NUMERIC,
    calculatedMetric?.data || null
  );

  // these configurations will be removed when changing to treemap
  const shouldTreemapUpdateConfig = useMemo(
    () =>
      !!(
        aggregator !== Aggregator.TOTAL ||
        showForecast ||
        !!trends.length ||
        !!dimensionsCols?.length ||
        (comparative && isComparative(comparative))
      ),
    [aggregator, showForecast, trends, dimensionsCols, comparative]
  );

  const handleRightPanelClose = useCallback(() => {
    setRightPanelOpen(false);
  }, []);

  const handleCachedReportClicked = useCallback(async () => {
    setNotificationDialogOpen(true);
  }, []);

  const handleNotificationConfirm = useCallback(
    async (settings: NotifyVia) => {
      setIsReportCachedBtnLoading(true);
      showSharedSnackbar({
        message: reportTxt.REPORT_CACHE_SNACKBAR_TEXT,
        variant: "info",
        autoHideDuration: 5000,
      });
      setNotificationDialogOpen(false);
      await cacheReport(settings);
    },
    [cacheReport, showSharedSnackbar]
  );

  const pageContent = useMemo(
    () => (
      <AnalyticsPageContent
        isCurrentUserEditor={isCurrentUserEditor}
        onCacheReportClicked={handleCachedReportClicked}
        onCancelReportRunClicked={() => {
          controller.current.abort();
        }}
        colKeySort={colKeySort}
        countFieldMissing={countFieldMissing}
        hasUnsavedChanges={hasUnsavedChanges || !name}
        forceRender={forceRender}
        formatter={calculatedMetric ? calculatedMetricFormatter : baseFormatter}
        leftDrawerTransitioning={leftDrawerTransitioning}
        renderKey={renderKey}
        reportError={reportError}
        reportReady={reportReady}
        duringCachingProcess={reportDuringCachingProcess || isReportCachedBtnLoading}
        reportQueryRunning={queryRunning}
        setColKeySort={setColKeySort}
        setPopoverAnchorEl={setPopoverAnchorEl}
        showForecast={showForecast}
        cacheThresholdMs={cacheThresholdMs}
      />
    ),
    [
      isCurrentUserEditor,
      handleCachedReportClicked,
      colKeySort,
      countFieldMissing,
      hasUnsavedChanges,
      name,
      forceRender,
      calculatedMetric,
      calculatedMetricFormatter,
      baseFormatter,
      isReportCachedBtnLoading,
      leftDrawerTransitioning,
      renderKey,
      reportError,
      reportReady,
      reportDuringCachingProcess,
      queryRunning,
      showForecast,
      cacheThresholdMs,
    ]
  );

  // Hotkeys disabled when popover/dialogs open
  useEffect(() => {
    if (
      googleDialogOpen ||
      filterDialogOpen ||
      openNameDialog ||
      popoverAnchorEl?.widget ||
      popoverAnchorEl?.mdCol ||
      popoverAnchorEl?.mdRow ||
      popoverAnchorEl?.mdUnused ||
      popoverAnchorEl?.mdCount ||
      saveAsComponentVisible ||
      metricSplitModalData ||
      createTemplateData.isCreateTemplateOpen
    ) {
      setHotKeysDisabled();
    } else {
      setHotKeysEnabled();
    }
  }, [
    filterDialogOpen,
    googleDialogOpen,
    popoverAnchorEl?.widget,
    popoverAnchorEl?.mdCol,
    popoverAnchorEl?.mdRow,
    popoverAnchorEl?.mdUnused,
    popoverAnchorEl?.mdCount,
    saveAsComponentVisible,
    setHotKeysDisabled,
    setHotKeysEnabled,
    metricSplitModalData,
    openNameDialog,
    createTemplateData.isCreateTemplateOpen,
  ]);

  const prevQuerySuccess = usePrevious(querySuccess);

  const numOfGroups = useMemo(
    () => dimensions?.filter((item) => item._visible && item._position === Positions.ROW).length || 0,
    [dimensions]
  );

  useEffect(() => {
    if (!report.data.draft || touched.has("rendererSelect") || report.data.config !== null) {
      return;
    }

    if (!reportStates.finishedFirstQueryRun) {
      if (numOfGroups > 1 && renderer === Renderer.STACKED_COLUMN_CHART) {
        dispatchReportConfig({ payload: { renderer: Renderer.TABLE } });
      } else if (numOfGroups < 2 && renderer === Renderer.TABLE) {
        dispatchReportConfig({ payload: { renderer: Renderer.STACKED_COLUMN_CHART } });
      }
    } else {
      const wasRerun = reportStates.finishedFirstQueryRun && !prevQuerySuccess && querySuccess;

      if (wasRerun && numOfGroups > 1) {
        dispatchReportConfig({ payload: { renderer: Renderer.TABLE } });
      }
    }
  }, [
    numOfGroups,
    renderer,
    reportStates.finishedFirstQueryRun,
    querySuccess,
    prevQuerySuccess,
    touched,
    dispatchReportConfig,
    report.data.draft,
    report.data.config,
  ]);

  const handleConfigChangedByAva = useCallback(() => {
    (async () => {
      const configForReset = mapReportConfigStoreToReportConfig(reportConfig);
      const newDimensions = await resetDimensions(true, configForReset);
      handleRun(undefined, newDimensions);
    })();
  }, [reportConfig, resetDimensions, handleRun]);

  useEffect(() => {
    if (prevReportConfig === undefined || !reportConfig.isGenerated) {
      return;
    }
    if (isEqual(prevReportConfig, reportConfig)) {
      return;
    }

    handleConfigChangedByAva();
  }, [reportConfig, prevReportConfig, handleConfigChangedByAva]);

  const handleComparativeChange = (value) => {
    if (comparative === ComparativeFeature.NONE) {
      setComparativeRequest(true);
      setComparativeDialogOpen(true);
    }
    dispatchReportConfig({ payload: { comparative: value } });
  };

  const resetReport = useCallback(
    async (newConfig: ReportConfig) => {
      const deletedDimensions = dimensions?.filter((d) => d._visible && d.data.type !== Metadata.DATETIME);
      deletedDimensions?.length && handleDelete(deletedDimensions);
      await handleResetReport(newConfig);
      reportData.current = undefined;
      reportDataWForecasts.current = undefined;
      dispatchReportStates({ type: ReportStateKind.REPORT_RESET });
      setReportError(undefined);
    },
    [dimensions, handleDelete, handleResetReport, reportData, reportDataWForecasts]
  );

  const handleRefreshReportCache = useCallback(async () => {
    try {
      setIsRefreshCacheBtnLoading(true);
      const response = await refreshCachedReport(api, customer.id, report.ref.id);
      if (response.status === 200) {
        setForceRender(true);
      }
    } catch (error: any) {
      if (error?.response?.status === 524) {
        return;
      } else {
        consoleErrorWithSentry(error);
        setForceRender(false);
        error.response.status = 500;
      }
    } finally {
      setForceRender(false);
      setIsRefreshCacheBtnLoading(false);
    }
  }, [api, customer.id, report.ref.id]);

  const renderReportActionButton = useCallback(() => {
    if (isCachedReport && !hasUnsavedChanges) {
      const disableRefreshCacheButton =
        !saveButtonDisabled ||
        userCachedReportWidget?.state === WidgetState.Processing ||
        isReportCacheRecentlyRefreshed;
      let refreshCacheTooltip = reportTxt.REPORT_CACHE_REFRESH_BTN_TOOLTIP;

      if (isRefreshCacheBtnLoading || userCachedReportWidget?.state === WidgetState.Processing) {
        refreshCacheTooltip = reportTxt.REPORT_CACHE_REFRESH_BTN_PROCESSING_TOOLTIP;
      } else if (isReportCacheRecentlyRefreshed) {
        refreshCacheTooltip = reportTxt.REPORT_CACHE_REFRESH_BTN_RECENTLY_UPDATED_TOOLTIP;
      }

      return (
        <Tooltip arrow title={refreshCacheTooltip}>
          <Box sx={{ flex: 1 }}>
            <LoadingButton
              variant="contained"
              color="primary"
              disabled={disableRefreshCacheButton}
              loading={isRefreshCacheBtnLoading}
              onClick={handleRefreshReportCache}
              sx={{
                borderRadius: 1,
                width: "100%",
                justifyContent: "space-between",
              }}
              mixpanelEventId="analytics.report.refresh"
            >
              {reportTxt.REFRESH_CACHE}
            </LoadingButton>
          </Box>
        </Tooltip>
      );
    }

    return (
      <Box sx={{ flex: 1 }}>
        <MixpanelButton
          variant="contained"
          color="primary"
          onClick={() => handleRun()}
          disabled={queryRunning || isGeneratingReportConfig}
          data-cy="run-report"
          sx={{
            borderRadius: 1,
            width: "100%",
            justifyContent: "space-between",
          }}
          mixpanelEventId="analytics.report.run"
        >
          <span>{reportText.RUN_REPORT}</span>
          <span>{reportTxt.RUN_REPORT_SHORTCUT}</span>
        </MixpanelButton>
      </Box>
    );
  }, [
    userCachedReportWidget?.state,
    handleRefreshReportCache,
    handleRun,
    hasUnsavedChanges,
    isCachedReport,
    isRefreshCacheBtnLoading,
    isReportCacheRecentlyRefreshed,
    queryRunning,
    saveButtonDisabled,
    isGeneratingReportConfig,
  ]);

  const isReportEmpty =
    !reportData.current?.getNumRecords() &&
    !reportDataWForecasts.current?.getNumRecords() &&
    !cachedReportData?.getNumRecords();

  const reportTouched = Boolean(report.data.config);

  const isEntitledAvaIcon = useIsFeatureEntitled("platform:ava:reports");
  const canHaveAva = useMemo(() => !!isEntitledAvaIcon, [isEntitledAvaIcon]);

  const hasDefaultReportConfig = Boolean(defaultReportConfig);

  const showAvaView =
    (isGeneratingReportConfig || !reportTouched || hasDefaultReportConfig) &&
    !queryRunning &&
    isReportEmpty &&
    !reportError;

  if (!customer || !dimensions) {
    return <CircularProgressLoader />;
  }

  const handleConfirmComparative = () => {
    dispatchReportConfig({ type: ReportConfigKind.ON_COMPARATIVE_MODE });
    sortComparativeDimensions();
    reportMixpanel.comparativeOn();
    handleClose();
  };

  return (
    <Box sx={{ margin: "-8px -16px 0", mb: -10 }}>
      <DoitConsoleTitle pageName="Reports" pageLevel1={name || reportText.NEW_REPORT} />
      <ReportHeader
        removeReportFromCache={removeReportFromCache}
        handleRevert={handleRevertReport}
        isCachedReport={isCachedReport}
        popoverAnchorEl={popoverAnchorEl}
        reportCacheRefreshedAt={rawCacheReport?.timeRefreshed.toDate()}
        reportError={reportError?.code}
        saveButtonDisabled={saveButtonDisabled}
        setGoogleDialogOpen={setGoogleDialogOpen}
        setIsDeleting={setIsDeleting}
        setPopoverAnchorEl={setPopoverAnchorEl}
        setRightPanelOpen={setRightPanelOpen}
        showRemoveFromCache={isCachedReport && reportCacheType === ReportCacheType.UserCached}
        wasRun={reportStates.finishedFirstQueryRun}
        isScheduledReport={isScheduled}
      />
      {smDown ? (
        <Paper
          variant="outlined"
          ref={chartContainerRef}
          sx={{
            overflowX: "scroll",
            height: chartContainerHeight,
          }}
        >
          <Box sx={{ minWidth: queryRunning || showAvaView ? "100%" : 768, height: "100%" }}>
            {canHaveAva && showAvaView ? (
              <AvaReportPrompt
                isGeneratingReportConfig={isGeneratingReportConfig}
                onIsGeneratingReportConfig={setIsGeneratingReportConfig}
              />
            ) : (
              pageContent
            )}
          </Box>
        </Paper>
      ) : (
        <>
          <DndProvider backend={HTML5Backend}>
            <Grid
              container
              ref={chartContainerRef}
              direction="row"
              wrap="nowrap"
              sx={{
                height: chartContainerHeight,
                display: "flex",
              }}
            >
              {/* LEFT */}
              <Grid
                sx={{
                  display: "flex",
                }}
              >
                <Box
                  sx={{
                    width: reportLeftDrawerOpen ? REPORT_LEFT_PANEL_WIDTH : REPORT_LEFT_PANEL_WIDTH_CLOSED,
                    transition: "width 0.3s",
                    borderRight: "1px solid",
                    borderRightColor: "general.divider",
                  }}
                  onTransitionEnd={(transitionEvent) => {
                    onTransitionEnd(transitionEvent);
                  }}
                >
                  {reportLeftDrawerOpen && (
                    <Stack
                      direction="column"
                      sx={{
                        height: "100%",
                      }}
                    >
                      <Stack
                        sx={{
                          overflowY: "auto",
                          height: `calc(100% - ${REPORT_LEFT_PANEL_BOTTOM_HEIGHT}px)`,
                          px: 3,
                          maxWidth: "100%",
                        }}
                      >
                        <DraggableGroupWrapper
                          resetReport={resetReport}
                          handleMetricSelect={handleMetricSelect}
                          handleAggregatorSelect={handleAggregatorSelect}
                          handleKubernetesMetricSelect={handleKubernetesMetricSelect}
                          isCSP={isCSP}
                          queryRunning={queryRunning}
                          handleResetLimits={handleResetLimits}
                          handleDelete={handleDelete}
                          handleAddDimension={handleAddDimension}
                          handleChangeLabels={handleChangeLabels}
                          handleFilterOperation={handleFilterPopoverOpened}
                          popoverAnchorEl={popoverAnchorEl}
                          setPopoverAnchorEl={setPopoverAnchorEl}
                          touched={touched}
                          setColKeySort={setColKeySort}
                          colSortDisabled={colSortDisabled}
                          handleReportError={handleReportError}
                          onChangeReportFilter={changeReportFilter}
                          handleCurrencyChange={handleCurrencyChange}
                        />
                      </Stack>
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: "center",
                          flex: 1,
                          borderTop: "1px solid",
                          borderTopColor: "general.divider",
                          px: 3,
                        }}
                      >
                        {renderReportActionButton()}
                        <IconButton
                          sx={{ ml: 1 }}
                          onClick={() => {
                            setReportLeftDrawerOpen(false);
                          }}
                        >
                          <ArrowLeft />
                        </IconButton>
                      </Stack>
                    </Stack>
                  )}
                  {!reportLeftDrawerOpen && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        height: "100%",
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          setReportLeftDrawerOpen(true);
                          setLeftDrawerTransitioning(true);
                        }}
                        sx={{
                          ml: 2,
                          mb: 2.5,
                          p: 0,
                        }}
                      >
                        <ArrowRight />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* MIDDLE */}
              <Grid
                container
                sx={{
                  alignContent: "flex-start",
                  flex: "1",

                  width: `calc(100% - ${
                    REPORT_LEFT_PANEL_WIDTH + 8 + (createTemplateData.isCreateTemplateOpen ? 398 : 0)
                  }px)`,
                }}
              >
                {/* COLUMNS */}
                <Grid size={12}>{renderSheetsDialog()}</Grid>
                <Grid sx={{ pt: 2.625, pb: 3.5, pl: 3, pr: 3.5 }} className={classes.middlePanelHeight} size={12}>
                  <Box className={classes.middlePanelHeight}>
                    <Stack direction="column" className={classes.middlePanelHeight}>
                      <Grid className={classes.topPanelConfig} size={12}>
                        <Grid
                          className={classes.topPanelConfigOption}
                          sx={{
                            minWidth: 195,
                          }}
                        >
                          <ReportViewSelect
                            shouldTreemapUpdateConfig={shouldTreemapUpdateConfig}
                            setTreemapDialogOpen={setTreemapDialogOpen}
                            disableTreemap={false}
                            touched={touched}
                          />
                        </Grid>
                        <Grid className={classes.topPanelConfigOption} data-testid="comparative-data">
                          <TextField
                            size="small"
                            label={reportText.COMPARATIVE_LABEL}
                            disabled={report.data.type === "preset" || queryRunning}
                            value={comparative}
                            onChange={(event) => {
                              handleComparativeChange(event.target.value);
                            }}
                            variant="outlined"
                            margin="dense"
                            select
                            fullWidth
                            slotProps={{
                              select: {
                                classes: {
                                  root: classes.selectOutlined,
                                },
                                MenuProps,
                              },
                            }}
                          >
                            {Object.values(comparativeValues).map((compOption) => (
                              <MenuItem
                                key={compOption.value}
                                value={compOption.value}
                                dense
                                disabled={
                                  ("renderer" in compOption && !compOption.renderer.includes(renderer)) || queryRunning
                                }
                              >
                                {compOption.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>

                      {/* PLOT */}
                      <Grid
                        data-testid="plot"
                        sx={{ overflow: "auto", pl: "4px", pr: "4px", flexBasis: "100%" }}
                        size={12}
                      >
                        {showLimitAlert && (
                          <Info setHidden={setHideLimitAlert} text={cloudAnalyticsText.LIMITS.ALERT_TEXT} />
                        )}
                        <Box
                          sx={{
                            minWidth: 768,
                            height: "100%",
                          }}
                        >
                          {canHaveAva && showAvaView ? (
                            <AvaReportPrompt
                              isGeneratingReportConfig={isGeneratingReportConfig}
                              onIsGeneratingReportConfig={setIsGeneratingReportConfig}
                            />
                          ) : (
                            chartContainerHeight && pageContent
                          )}
                        </Box>
                      </Grid>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>

              {isDoitEmployee && createTemplateData.isCreateTemplateOpen && (
                <Grid sx={{ pl: 1 }}>
                  <TemplateCreate />
                </Grid>
              )}

              {/* RIGHT */}
              <ReportRightMenu
                metadata={dimensions}
                open={rightPanelOpen}
                handleClose={handleRightPanelClose}
                extendedMetric={extendedMetrics?.find((m) => m.key === extendedMetric)?.label ?? ""}
                calculatedMetricFormatter={calculatedMetricFormatter}
              />
              <ReportQuickView />
            </Grid>
          </DndProvider>

          {metricSplitModalData && <SplitCostModal />}

          {selected && (
            <FilterDialog
              open={filterDialogOpen}
              onClose={handleClose}
              selected={selected}
              onCancel={handleDelete}
              onSave={handleChangeFilter}
              cloudProviders={selected?.id !== `${Metadata.FIXED}:${FixedFilters.CLOUD}` ? cloudProviders : null}
            />
          )}
        </>
      )}
      <SimpleDialog
        open={comparativeDialogOpen}
        onConfirm={handleConfirmComparative}
        title={comparativeDialogText.COMPARATIVE_DIALOG_HEADER}
        onCancel={() => {
          dispatchReportConfig({ payload: { comparative: ComparativeFeature.NONE } });

          setComparativeRequest(false);
          handleClose();
        }}
        confirmButtonText={comparativeDialogText.DIALOG_ACTION_BUTTON}
      >
        <DialogContent>
          <DialogContentText>
            {comparativeDialogText.COMPARATIVE_DIALOG_BODY}
            <br />
            <br />
            {comparativeDialogText.COMPARATIVE_DIALOG_BULLETS.map((t, i) => (
              <Fragment key={i}>
                &nbsp;{t}
                <br />
              </Fragment>
            ))}
          </DialogContentText>
        </DialogContent>
      </SimpleDialog>
      <TreemapConfirmDialog
        open={treemapDialogOpen}
        onClose={handleClose}
        onConfirm={handleChangeToTreemap}
        cols={dimensionsCols?.map((md) => `"${md.data.label}"`) ?? []}
      />
      <SimpleDialog
        title={metricText.METRIC_DIALOG_TITLE(selectedCalcMetric?.data.name)}
        open={calcMetricsDialogOpen}
        onCancel={() => {
          setSelectedCalcMetric(null);
          handleClose();
        }}
        onConfirm={handleCalculatedMetric}
        confirmButtonText={metricText.METRIC_DIALOG_CONFIRM}
      >
        <DialogContent>
          <DialogContentText>
            {metricText.METRIC_DIALOG_CONTENT}
            <List dense>
              <ListItem>
                <ListItemText primary={metricText.METRIC_DIALOG_RESTRICTIONS} />
              </ListItem>
            </List>
          </DialogContentText>
        </DialogContent>
      </SimpleDialog>
      <MetricFiltersDialog
        calculatedMetric={calculatedMetric}
        extendedMetric={extendedMetrics?.find((m) => m.key === extendedMetric)?.label}
        isCSP={isCSP}
        metricFilter={getMetricFilter()}
        onCancel={handleCancelMetricFilters}
        onConfirm={handleMetricFilters}
        open={metricFiltersDialogOpen}
        selected={selected}
      />

      <LimitsFilterDialog
        origLimits={dimensions?.filter((m) => m._visible && m._position === Positions.ROW)}
        isCSP={isCSP}
        calculatedMetric={calculatedMetric}
        extendedMetric={extendedMetrics?.find((m) => m.key === extendedMetric)?.label}
        metric={metric}
        onCancel={handleCancelLimits}
        onConfirm={onLimitsFilterConfirm}
        open={limitsFilterDialogOpen}
        selected={selected}
        initialAggregation={limitAggregation}
      />
      <NotificationDialog
        open={notificationDialogOpen}
        onClose={() => {
          setNotificationDialogOpen(false);
        }}
        onConfirm={handleNotificationConfirm}
        isSlackConnected={isSlackConnected}
        slackLoading={slackLoading}
      />
      {saveAsComponentVisible && (
        <SaveAsComponent
          saveAsTitle={reportText.SAVE_NEW}
          onSaveAs={(name) =>
            handleSaveAsComponentSave(name, () => {
              setSaveAsComponentVisible(false);
            })
          }
          textFieldProps={{
            helperText: reportText.SAVE_AS_HELPER_TEXT,
            label: reportText.SAVE_AS_LABEL,
          }}
          successMessage={reportText.SUCCESSFULLY_SAVED}
          disabled={false}
        />
      )}
      {openNameDialog && (
        <SaveDialog
          title={reportTxt.NAME_BEFORE_SAVING}
          open={openNameDialog}
          onClose={handleClose}
          onSave={(name) =>
            handleSaveName(name, () => {
              setOpenNameDialog(false);
            })
          }
          textFieldProps={{ label: reportTxt.REPORT_NAME }}
        />
      )}
    </Box>
  );
};

export default Report;
